import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Users, Star, ArrowRight, RotateCcw, Save, Trash2 } from "lucide-react";

const LineupBuilder = ({ players, game, team }) => {
  const [starting5, setStarting5] = useState([]);
  const [bench, setBench] = useState([]);
  const [savedLineups, setSavedLineups] = useState([]);
  const [lineupName, setLineupName] = useState("");
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    // Initialize with all players on bench
    if (players && players.length > 0) {
      setBench([...players]);
      setStarting5([]);
    }

    // Load saved lineups from localStorage
    const saved = localStorage.getItem(`lineups_${team?.id}`);
    if (saved) {
      setSavedLineups(JSON.parse(saved));
    }
  }, [players, team?.id]);

  const moveToStarting = (player) => {
    if (starting5.length >= 5) return;

    setStarting5([...starting5, player]);
    setBench(bench.filter((p) => p.id !== player.id));
  };

  const moveToBench = (player) => {
    setBench([...bench, player]);
    setStarting5(starting5.filter((p) => p.id !== player.id));
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newStarting = [...starting5];
    [newStarting[index], newStarting[index - 1]] = [
      newStarting[index - 1],
      newStarting[index],
    ];
    setStarting5(newStarting);
  };

  const moveDown = (index) => {
    if (index === starting5.length - 1) return;
    const newStarting = [...starting5];
    [newStarting[index], newStarting[index + 1]] = [
      newStarting[index + 1],
      newStarting[index],
    ];
    setStarting5(newStarting);
  };

  const reset = () => {
    setBench([...players]);
    setStarting5([]);
  };

  const saveLineup = () => {
    if (!lineupName.trim()) {
      alert("Please enter a lineup name");
      return;
    }

    const newLineup = {
      id: Date.now(),
      name: lineupName,
      starting5: starting5.map((p) => p.id),
      createdAt: new Date().toISOString(),
    };

    const updated = [...savedLineups, newLineup];
    setSavedLineups(updated);
    localStorage.setItem(`lineups_${team?.id}`, JSON.stringify(updated));

    setLineupName("");
    setShowSaveModal(false);
  };

  const loadLineup = (lineup) => {
    const starting = lineup.starting5
      .map((id) => players.find((p) => p.id === id))
      .filter(Boolean);

    setStarting5(starting);
    setBench(players.filter((p) => !lineup.starting5.includes(p.id)));
  };

  const deleteLineup = (id) => {
    const updated = savedLineups.filter((l) => l.id !== id);
    setSavedLineups(updated);
    localStorage.setItem(`lineups_${team?.id}`, JSON.stringify(updated));
  };

  const PlayerCard = ({ player, onMove, showMoveButtons, position }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 border-gray-200 dark:border-gray-700 
                 hover:border-orange-400 dark:hover:border-orange-600 transition-all shadow-sm hover:shadow-md"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {position !== undefined && (
            <div
              className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-600 text-white 
                           flex items-center justify-center font-bold text-sm"
            >
              {position + 1}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 dark:text-white truncate">
              {player.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              #{player.jersey_number || "—"} •{" "}
              {player.pointsPerGame?.toFixed(1) || "0.0"} PPG
            </p>
          </div>
        </div>

        <div className="flex gap-1">
          {showMoveButtons && position !== undefined && (
            <>
              <button
                onClick={() => moveUp(position)}
                disabled={position === 0}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move up"
              >
                ↑
              </button>
              <button
                onClick={() => moveDown(position)}
                disabled={position === starting5.length - 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move down"
              >
                ↓
              </button>
            </>
          )}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onMove(player)}
            className="p-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors"
          >
            <ArrowRight size={16} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 sm:p-8 border border-gray-100 dark:border-gray-700 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-purple-500/10 dark:bg-purple-500/20">
            <Users size={22} className="text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Lineup Builder
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select your starting 5
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={reset}
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gray-100 dark:bg-gray-700 
                       text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <RotateCcw size={18} /> Reset
          </motion.button>

          {starting5.length === 5 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowSaveModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-purple-600 hover:bg-purple-700 
                         text-white font-semibold transition-colors shadow-sm"
            >
              <Save size={18} /> Save Lineup
            </motion.button>
          )}
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Starting 5 */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Star
              className="text-orange-600 dark:text-orange-400"
              size={20}
              fill="currentColor"
            />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Starting 5
            </h3>
            <span
              className={`ml-auto text-sm font-semibold ${
                starting5.length === 5
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {starting5.length}/5
            </span>
          </div>

          <div
            className="space-y-3 min-h-[400px] bg-gradient-to-br from-orange-50 to-amber-50 
                         dark:from-orange-950/20 dark:to-amber-950/10 rounded-2xl p-4 border-2 border-dashed 
                         border-orange-200 dark:border-orange-800"
          >
            <AnimatePresence>
              {starting5.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-600">
                  <p className="text-center">
                    Select players from the bench
                    <br />
                    to build your starting lineup
                  </p>
                </div>
              ) : (
                starting5.map((player, index) => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    onMove={moveToBench}
                    showMoveButtons={true}
                    position={index}
                  />
                ))
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Bench */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="text-blue-600 dark:text-blue-400" size={20} />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Bench
            </h3>
            <span className="ml-auto text-sm font-semibold text-gray-500 dark:text-gray-400">
              {bench.length} players
            </span>
          </div>

          <div
            className="space-y-3 max-h-[400px] overflow-y-auto bg-gray-50 dark:bg-gray-900/50 
                         rounded-2xl p-4 border-2 border-gray-200 dark:border-gray-700"
          >
            <AnimatePresence>
              {bench.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-600">
                  <p>All players in starting lineup</p>
                </div>
              ) : (
                bench.map((player) => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    onMove={moveToStarting}
                    showMoveButtons={false}
                  />
                ))
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Saved Lineups */}
      {savedLineups.length > 0 && (
        <div className="border-t dark:border-gray-700 pt-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Saved Lineups
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {savedLineups.map((lineup) => (
              <motion.div
                key={lineup.id}
                whileHover={{ scale: 1.02 }}
                className="bg-gray-50 dark:bg-gray-900/50 rounded-2xl p-4 border-2 border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h4 className="font-bold text-gray-900 dark:text-white">
                    {lineup.name}
                  </h4>
                  <button
                    onClick={() => deleteLineup(lineup.id)}
                    className="p-1 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/30 
                               text-red-600 dark:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {new Date(lineup.createdAt).toLocaleDateString()}
                </p>
                <button
                  onClick={() => loadLineup(lineup)}
                  className="w-full py-2 px-3 bg-purple-600 hover:bg-purple-700 text-white 
                             rounded-xl text-sm font-semibold transition-colors"
                >
                  Load Lineup
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Save Modal */}
      <AnimatePresence>
        {showSaveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-700 p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Save Lineup
              </h3>
              <input
                type="text"
                value={lineupName}
                onChange={(e) => setLineupName(e.target.value)}
                placeholder="Enter lineup name..."
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-2xl 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                           focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="flex-1 py-3 rounded-2xl border-2 border-gray-200 dark:border-gray-700 
                             text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveLineup}
                  className="flex-1 py-3 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white 
                             font-semibold transition-colors shadow-sm"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LineupBuilder;
